import { Global } from '@emotion/react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import AxisConfigurationModalConfigurationControls from 'components/molecules/PackArchitecture/AxisConfigurationModalConfigurationControls';
import AxisConfigurationModalFooter from 'components/molecules/PackArchitecture/AxisConfigurationModalFooter';
import AxisConfigurationModalPreview from 'components/molecules/PackArchitecture/AxisConfigurationModalPreview';
import { getConfigurationTitle, getDiagnosticPageName } from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import {
    AXIS_CONFIGURATION_MODAL_CLASS,
    axisConfigurationModalGlobalStyles,
} from 'components/molecules/PackArchitecture/AxisConfigurationModal.styles';
import type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import { useAxisConfigurationModalState } from 'components/molecules/PackArchitecture/useAxisConfigurationModalState';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { PPA_AXIS_CONFIG_MODAL } from 'constants/ppaTestIds';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_DIMENSION_AXIS_CONFIG_APPLIED,
    PPA_DIMENSION_AXIS_CONFIG_CANCELLED,
    PPA_DIMENSION_AXIS_CONFIG_RESET,
} from 'utils/mixpanelEventNames';

export type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';

type AxisConfigurationModalProps = {
    open: boolean;
    dimensionLabel: string;
    controlsMeta?: AxisGroupByControlsMeta | null;
    initialConfig?: AxisConfiguration;
    chartRequest?: PricePackArchitectureRequest | null;
    whitespacePreviewRequest?: Record<string, unknown> | null;
    useWhitespacePreview?: boolean;
    /** `column` for X-axis dimensions (default); `row` for Y-axis attribute-bucket dimensions (e.g. Pack_Range). */
    whitespacePreviewAxisKind?: 'column' | 'row';
    hideGroupRestAsOther?: boolean;
    xAxis?: string;
    apiTotalCount?: number;
    onClose: () => void;
    onApply?: (config: AxisConfiguration) => void;
};

const AxisConfigurationModal = ({
    open,
    dimensionLabel,
    controlsMeta,
    initialConfig,
    chartRequest = null,
    whitespacePreviewRequest = null,
    useWhitespacePreview = false,
    whitespacePreviewAxisKind = 'column',
    hideGroupRestAsOther = false,
    xAxis = '',
    apiTotalCount = 0,
    onClose,
    onApply,
}: AxisConfigurationModalProps) => {
    const pageName = getDiagnosticPageName(useWhitespacePreview);

    const {
        config,
        baselineConfig,
        effectiveTotalCount,
        isPreviewLoading,
        pauseShowTopPreview,
        setPauseShowTopPreview,
        showOwnEntityFirstOption,
        hasApplyChanges,
        hasResetChanges,
        previewResult,
        updateConfig,
        handleReset,
        handleCancel,
        handleApply,
    } = useAxisConfigurationModalState({
        open,
        dimensionLabel,
        controlsMeta,
        initialConfig,
        chartRequest,
        whitespacePreviewRequest,
        useWhitespacePreview,
        whitespacePreviewAxisKind,
        hideGroupRestAsOther,
        xAxis,
        apiTotalCount,
        onClose,
        onApply,
    });

    const wrappedHandleReset = () => {
        handleReset();
        trackEventMixpanel(PPA_DIMENSION_AXIS_CONFIG_RESET, { page_name: pageName, axis_name: dimensionLabel });
    };

    const wrappedHandleCancel = () => {
        handleCancel();
        trackEventMixpanel(PPA_DIMENSION_AXIS_CONFIG_CANCELLED, { page_name: pageName, axis_name: dimensionLabel });
    };

    const wrappedHandleApply = () => {
        handleApply();
        const axisConfigPayload = {
            axis_name: dimensionLabel,
            previous_sort_by: baselineConfig.sortBy,
            selected_sort_by: config.sortBy,
            previous_sort_order: baselineConfig.sortOrder,
            selected_sort_order: config.sortOrder,
            previous_show_top: baselineConfig.showTop,
            selected_show_top: config.showTop,
            previous_group_rest_as_others: baselineConfig.groupRestAsOther,
            selected_group_rest_as_others: config.groupRestAsOther,
            previous_show_my_dimension_first: baselineConfig.showOwnVendorFirst,
            selected_show_my_dimension_first: config.showOwnVendorFirst,
        };
        trackEventMixpanel(PPA_DIMENSION_AXIS_CONFIG_APPLIED, { page_name: pageName, ...axisConfigPayload });
    };

    return (
        <>
            <Global styles={axisConfigurationModalGlobalStyles} />
            <Modal
                title={getConfigurationTitle(dimensionLabel, controlsMeta?.pluralLabel)}
                open={open}
                onCancel={wrappedHandleCancel}
                closeIcon={<CloseOutlined className="text-sm text-toolbarLabelGrey" />}
                width={720}
                footer={
                    <AxisConfigurationModalFooter
                        hasApplyChanges={hasApplyChanges}
                        hasResetChanges={hasResetChanges}
                        onReset={wrappedHandleReset}
                        onCancel={wrappedHandleCancel}
                        onApply={wrappedHandleApply}
                    />
                }
                destroyOnClose
                centered
                wrapClassName={AXIS_CONFIGURATION_MODAL_CLASS}
                modalRender={(modalNode) => <div data-testid={PPA_AXIS_CONFIG_MODAL}>{modalNode}</div>}
            >
                <div className="flex h-full min-h-0">
                    {controlsMeta ? (
                        <AxisConfigurationModalConfigurationControls
                            config={config}
                            controlsMeta={controlsMeta}
                            totalCount={effectiveTotalCount}
                            valueSalesPercent={previewResult.valueSalesPercent}
                            volumeSalesPercent={previewResult.volumeSalesPercent}
                            isPreviewLoading={isPreviewLoading && !pauseShowTopPreview}
                            hideGroupRestAsOther={hideGroupRestAsOther}
                            hideShowTopStats={useWhitespacePreview && whitespacePreviewAxisKind === 'row'}
                            onChange={updateConfig}
                            onShowTopPreviewPauseChange={setPauseShowTopPreview}
                        />
                    ) : null}
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-axisModalBg pl-6 pt-4">
                        <AxisConfigurationModalPreview
                            config={config}
                            ownDimensionSortLabel={controlsMeta?.ownDimensionSort?.label}
                            previewResult={previewResult}
                            displayTotalCount={effectiveTotalCount}
                            showOwnEntityFirstOption={showOwnEntityFirstOption}
                            isLoading={isPreviewLoading && !pauseShowTopPreview}
                            onChange={updateConfig}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AxisConfigurationModal;
